// React
import { useCallback } from 'react';

// Models
import { useStores } from 'models';
import { BaseRawHtml, FormTextArea, FormTextField } from 'components';

// Antd
import { ColumnsType } from 'antd/es/table';
import { Radio, Space } from 'antd';

// Interfaces
import {
    Header,
    IGetAssesseeAdditionalQuestionAttrs,
    IGetPICAreaAdditionalQuestionAttrs,
    Question,
    Body,
    Result,
    IUpdateAssesseeAdditionalQuestionAttrs,
} from 'interfaces/back-office/personal-data/additional-question';
import { IAdditionalQuestionHookProps } from './interfaces';

// Constant
import { ANSWER_TYPES } from 'constant/shared.constant';
import { cloneDeep } from 'lodash';
const { SHORT_ANSWER, PARAGRAPH, MULTIPLE_CHOICE } = ANSWER_TYPES;

const useAdditionalQuestion = () => {
    // Hook
    const {
        additionalQuestion: {
            additionalQuestion_SET_LOADING,
            additionalQuestion_SET_SAVE_LOADING,
            additionalQuestion_fetchAdditionalQuestionAsAssessee: fetchAdditionalQuestionAsAssessee,
            additionalQuestion_fetchAdditionalQuestionAsPICArea: fetchAdditionalQuestionAsPICArea,
            additionalQuestion_additionalQuestion,
            additionalQuestion_isLoading,
            additionalQuestion_isSaveLoading,
            additionalQuestion_SET_ADDITIONAL_QUESTION,
            additionalQuestion_updateAdditionalQuestion: updateAdditionalQuestion
        },
    } = useStores();

    /**
     * @description Fetch additional question as Assessee
     *
     * @param {IGetAssesseeAdditionalQuestionAttrs} payload
     *
     * @return {Promise<void>} Promise<void>
     */
    const additionalQuestion_fetchAdditionalQuestionAsAssessee = useCallback(
        (payload: IGetAssesseeAdditionalQuestionAttrs): Promise<void> => {
            return new Promise((resolve, reject) => {
                fetchAdditionalQuestionAsAssessee({
                    requestConfig: payload.requestConfig,
                    onSuccess: resolve,
                    onError: reject,
                });
            });
        },
        [],
    );

    /**
     * @description Fetch additional question as PIC Area
     *
     * @param {} payload
     *
     * @return {Promise<void>} Promise<void>
     */
    const additionalQuestion_fetchAdditionalQuestionAsPICArea = useCallback(
        (payload: IGetPICAreaAdditionalQuestionAttrs): Promise<void> => {
            return new Promise((resolve, reject) => {
                fetchAdditionalQuestionAsPICArea({
                    params: payload.params,
                    requestConfig: payload.requestConfig,
                    onSuccess: resolve,
                    onError: reject,
                });
            });
        },
        [],
    );

    /**
     * @description Render column table
     *
     * @param {Header[]} headers
     * 
     * @param {boolean} isChildren
     *
     * @return {ColumnsType<any>} columns
     */
    const additionalQuestion_MapColumns = useCallback((headers: Header[], qGroupId: number, isReadOnly: boolean, isChildren?: boolean): ColumnsType<any> => {
        return headers.map((header) => {
            return {
                key: header.headerId!,
                dataIndex: header.headerId!,
                width: header.type === "Pertanyaan" ? 400 : 300,
                title: header.headerName,
                children: !!header.child.length ? additionalQuestion_MapColumns(header.child, qGroupId, true) : [],
                render: (_, record: Body) => additionalQuestion_render(record, header.headerId!, qGroupId, isReadOnly ,isChildren),
            };
        });
    }, []);

    /**
     * @description Conditional rendering question/answer
     *
     * @param {Body} record
     * 
     * @param {number} headerId
     * 
     * @param {boolean} isChildren
     *
     * @return {void} void
     */
    const additionalQuestion_render = useCallback((record: Body, headerId: number, qGroupId: number, isReadOnly: boolean, isChildren?: boolean,) => {
        const res = record.result.find((item) => {
            if(isChildren) {
                return item.parentHeaderId === headerId 
            } else {
                return item.headerId === headerId
            }
        });
        

        if(res) {
            if (!res?.answers) {
                return additionalQuestion_renderQuestion(res?.questions!);
            } else {
                return additionalQuestion_renderAnswer(record, res, qGroupId, isReadOnly);
            }
        }

        // eslint-disable-next-line
    }, []);

    /**
     * @description Update Answer
     * 
     * @param {number} qGroupId
     * 
     * @param {number} rowId
     * 
     * @param {number} headerId
     * 
     * @param {number} qTypeId
     * 
     * @param {any} value
     */
    const additionalQuestion_onUpdateAnswser = useCallback((qGroupId: number, rowId: number, headerId: number, qTypeId: number, value: any) => {
        const data = cloneDeep(additionalQuestion_additionalQuestion)
        const currentPersonalQuestion = data.personalQuestions.find(item => item.qGroupId === qGroupId)
        const currentResult = currentPersonalQuestion?.body.find(item => item.rowId === rowId)?.result.find(item => item.headerId === headerId)

        if(qTypeId === MULTIPLE_CHOICE) {
            currentResult!.answers!.answerModel[0]!.answerDetails.map(item => {
                if(item.answerDetailsCode === value) {
                    item.userAnswer = {
                        transactionAssessmentAnswerId: null,
                        answerDescription: value
                    }
                } else {
                    item.userAnswer = null
                }
                return item
            })
        } else {
            currentResult!.answers!.answerModel[0]!.userAnswerText = value!
        }

        const modifiedData = data.personalQuestions.map(item => {
            if(item.qGroupId === qGroupId) {
                item.body.map(res => {
                    if(res.rowId === rowId) {
                        res.result.map(ans => {
                            if(ans.headerId === headerId){
                                ans = currentResult as any
                            }
                        })
                    }
                })
            }
            return item
        })

        additionalQuestion_SET_ADDITIONAL_QUESTION({
            personalQuestions: modifiedData as any
        })

        // eslint-disable-next-line
    }, [])   

    /**
     * @description Render question
     *
     * @param {Question} record
     *
     * @return {HTMLComponent} Component
     */
    const additionalQuestion_renderQuestion = useCallback((record: Question) => {
        switch (record.qtypeId) {
            case 1:
                return <BaseRawHtml html={record.questionDesc!} />;
        }

        // eslint-disable-next-line
    }, []);

    /**
     * @description Render Answer
     *
     * @param {Body} record
     * 
     * @param {Result} result
     * 
     * @param {number} qGroupId
     * 
     * @param {boolean} isReadOnly
     * 
     * @return {HTMLComponent} Component
     */
    const additionalQuestion_renderAnswer = useCallback((record: Body, result: Result, qGroupId: number, isReadOnly: boolean) => {
        const answer = result?.answers?.answerModel[0]
        if (answer) {
            switch (answer.atypeId) {
                case SHORT_ANSWER:
                    return (
                        <FormTextField
                            disabled={isReadOnly}
                            defaultValue={answer.userAnswerText || ''}
                            onChange={event => {
                                additionalQuestion_onUpdateAnswser(qGroupId, record.rowId!, result.headerId!, SHORT_ANSWER, event.target.value)
                            }}
                        />
                    );
                case PARAGRAPH:
                    return (
                        <FormTextArea
                            showCount
                            maxLength={300}
                            disabled={isReadOnly}
                            defaultValue={answer.userAnswerText || ''}
                            onChange={event => {
                                additionalQuestion_onUpdateAnswser(qGroupId, record.rowId!, result.headerId!, PARAGRAPH, event.target.value)
                            }}
                        />
                    )
                case MULTIPLE_CHOICE:
                    const value = answer.answerDetails.find(item => item.userAnswer !== null)
                    return (
                        <div className="flex flex-col gap-2">
                            <Radio.Group value={value?.userAnswer?.answerDescription} disabled={isReadOnly}>
                                <Space direction={'horizontal'} align="start">
                                    {!!answer.answerDetails.length &&
                                        answer.answerDetails.map(option => (
                                            <Radio value={option.answerDetailsCode} key={option.answerDetailsCode} 
                                            onChange={event => {
                                                additionalQuestion_onUpdateAnswser(qGroupId, record.rowId!, result.headerId!, MULTIPLE_CHOICE, event.target.value)
                                            }}
                                            >
                                                {option.answerDetailsName}
                                            </Radio>
                                        ))}
                                </Space>
                            </Radio.Group>
                        </div>
                    );
            }
        }

    // eslint-disable-next-line
    }, []);

    /**
     * @description Update Additional Question as Assessee
     * 
     * @param {IUpdateAssesseeAdditionalQuestionAttrs} payload
     * 
     * @return {Promise<void>} Promise<void>
     */
    const additionalQuestion_updateAdditionalQuestion = useCallback((payload: IUpdateAssesseeAdditionalQuestionAttrs): Promise<void> => {
        return new Promise((resolve, reject) => {
            updateAdditionalQuestion({
                body: { ...payload.body },
                requestConfig: payload.requestConfig,
                onSuccess: resolve,
                onError: reject,
            })
        })

    // eslint-disable-next-line
    }, [])

    return {
        additionalQuestion_SET_LOADING,
        additionalQuestion_SET_SAVE_LOADING,
        additionalQuestion_fetchAdditionalQuestionAsAssessee,
        additionalQuestion_fetchAdditionalQuestionAsPICArea,
        additionalQuestion_additionalQuestion,
        additionalQuestion_isLoading,
        additionalQuestion_isSaveLoading,
        additionalQuestion_renderQuestion,
        additionalQuestion_MapColumns,
        additionalQuestion_updateAdditionalQuestion,
    };
};

export { useAdditionalQuestion };
